/**
 * <collection-card-slider>
 * Loaded by sections/collection-card-slider.liquid
 *
 * The scrolling itself is native — overflow-x plus CSS scroll-snap, with the
 * snap stops sitting on page boundaries (see the section's scoped style block).
 * This component owns only the bullet row underneath it:
 *
 *   - it builds one bullet per page, and only when the track actually overflows
 *   - it maps the scroll position onto a continuous position along that row, so
 *     the active pill expands and the neighbouring dot contracts *while* the
 *     finger moves, rather than flipping at the end of a swipe
 *   - tapping a bullet scrolls to that page
 *
 * Bullets start out `hidden` in the markup, so a failure here leaves a plain
 * scrolling row rather than a dead control.
 */
if (!customElements.get('collection-card-slider')) {
  customElements.define(
    'collection-card-slider',
    class CollectionCardSlider extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-ccs-track]');
        this.dotList = this.querySelector('[data-ccs-dots]');
        if (!this.track || !this.dotList) return;

        this.cards = Array.from(this.track.children);
        this.dots = [];
        this.frame = null;

        this.handleScroll = this.handleScroll.bind(this);
        this.handleDotClick = this.handleDotClick.bind(this);

        this.track.addEventListener('scroll', this.handleScroll, { passive: true });
        this.dotList.addEventListener('click', this.handleDotClick);

        // A ResizeObserver rather than the theme's `on:debounced-resize`, because
        // it also catches the first correct measurement: `lazy-stylesheet-attrs`
        // hands this section's stylesheet `media="print"` below position 2, so
        // the deferred script can run while the cards are still unstyled. The
        // observer fires again the moment the real layout lands. main.js uses
        // ResizeObserver in ten places, so this is house style too.
        this.resizeObserver = new ResizeObserver(() => this.refresh());
        this.resizeObserver.observe(this.track);

        this.refresh();
      }

      disconnectedCallback() {
        if (!this.track) return;

        this.track.removeEventListener('scroll', this.handleScroll);
        this.dotList.removeEventListener('click', this.handleDotClick);
        this.resizeObserver.disconnect();
        if (this.frame) cancelAnimationFrame(this.frame);
      }

      /**
       * Cards per page — 2 on mobile, the cards-per-row setting on desktop.
       * Read from the custom property the section emits, so this number and the
       * scroll-snap stops that have to agree with it are declared together.
       * @returns {number}
       */
      get pageSize() {
        const declared = parseInt(getComputedStyle(this).getPropertyValue('--ccs-page-size'), 10);
        return Number.isNaN(declared) || declared < 1 ? 1 : declared;
      }

      /**
       * Rebuild the bullet row for the current viewport. Safe to call repeatedly.
       */
      refresh() {
        const pages = Math.ceil(this.cards.length / this.pageSize);
        const overflows = this.track.scrollWidth - this.track.clientWidth > 1;

        if (!overflows || pages < 2) {
          this.dotList.hidden = true;
          this.dotList.replaceChildren();
          this.dots = [];
          return;
        }

        if (pages !== this.dots.length) this.buildDots(pages);
        this.dotList.hidden = false;
        this.update();
      }

      /**
       * @param {number} pages - Number of bullets to render.
       */
      buildDots(pages) {
        const label = this.dataset.pageLabel || 'Page [page]';

        this.dots = Array.from({ length: pages }, (item, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'collection-card-slider__dot';
          dot.dataset.index = index;
          dot.setAttribute('aria-label', label.replace('[page]', index + 1));
          return dot;
        });

        this.dotList.replaceChildren(...this.dots);
      }

      handleScroll() {
        if (this.frame) return;

        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      }

      /**
       * Map the scroll offset onto the bullet row and weight each bullet by how
       * close it is to that point: 1 when it is the resting page, 0 once the
       * scroll is a whole page away, and everything in between mid-swipe.
       *
       * Progress is taken from the scrollable distance rather than from a card
       * index, so max scroll lands exactly on the last bullet even though the
       * final page's snap stop is an `end` alignment.
       */
      update() {
        if (!this.dots.length) return;

        const distance = this.track.scrollWidth - this.track.clientWidth;
        const progress = distance > 0 ? Math.abs(this.track.scrollLeft) / distance : 0;
        const position = progress * (this.dots.length - 1);
        const active = Math.round(position);

        this.dots.forEach((dot, index) => {
          const weight = Math.max(0, 1 - Math.abs(position - index));

          dot.style.setProperty('--ccs-dot-weight', weight.toFixed(3));
          if (index === active) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });
      }

      /**
       * @param {object} evt - Click event.
       */
      handleDotClick(evt) {
        const dot = evt.target.closest('.collection-card-slider__dot');
        if (!dot) return;

        const card = this.cards[Number(dot.dataset.index) * this.pageSize];
        if (!card) return;

        // scrollIntoView honours the track's scroll-padding, so the card lands
        // on the container's inner edge rather than under it. `block: 'nearest'`
        // keeps the page from scrolling vertically at the same time.
        card.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
      }
    }
  );
}
